import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { UserPlus, ArrowRight } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function PublicSignup() {
  const { signup } = useCustomerAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", password: "", delivery_address: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const res = await signup({ tenant_slug: "abrah", ...form });
      if (res.pendingVerification) {
        try { sessionStorage.setItem("aeoibcms_pending_verify_email", res.email); } catch { /* ignore */ }
        if (res.emailDeliveryFailed) {
          // Email backend not configured / temporarily unavailable — steer the
          // user toward the verify screen anyway so they can enter the code
          // (surfaced inline in dev, or delivered later once ops flips the key).
          toast.error(res.message || "We couldn't deliver the code. Please try again shortly.");
        } else {
          toast.success("Check your email for a 6-digit code");
        }
        nav("/site/verify", {
          replace: true,
          state: {
            email: res.email,
            next: loc.state?.next || "/site/account",
            debugOtp: res.debugOtp || null,
            emailDeliveryFailed: !!res.emailDeliveryFailed,
          },
        });
        return;
      }
      toast.success(`Welcome, ${form.full_name.split(" ")[0]}!`);
      const next = loc.state?.next || "/site/account";
      nav(next, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">
          <UserPlus size={12} /> Customer account
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-4 text-gray-900">
          Create your Abrah account
        </h1>
        <p className="text-sm text-gray-600 mt-2">
          Track your orders, save delivery details, apply for jobs and stay in touch.
        </p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <GoogleSignInButton intent="customer" label="Continue with Google" />
          <div className="relative flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-[10px] uppercase tracking-widest text-gray-400">or sign up with email</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Full name *</Label>
              <Input
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="signup-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email *</Label>
                <Input
                  required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="signup-email"
                />
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+256 …"
                  data-testid="signup-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Password *</Label>
              <Input
                required type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="signup-password"
              />
              <div className="text-[11px] text-gray-500 mt-1">Minimum 6 characters.</div>
            </div>
            <div>
              <Label className="text-xs">Delivery address (optional)</Label>
              <Input
                value={form.delivery_address}
                onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                placeholder="Plot 4, Kampala Road"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="signup-submit">
              {busy ? "Creating account…" : "Create account"} <ArrowRight size={14} className="ml-1" />
            </Button>
          </form>
          <div className="mt-5 pt-5 border-t border-gray-100 text-sm text-center text-gray-600">
            Already have an account?{" "}
            <Link
              to="/site/login"
              state={loc.state}
              className="text-emerald-800 font-semibold hover:underline"
              data-testid="signup-login-link">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
